import React, { Component, ErrorInfo, ReactNode } from 'react';
import { SACRED_COLORS } from '../utils/sacredTheme';

/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs errors, and displays a fallback UI instead of crashing the app.
 * 
 * - Prevents full app crashes
 * - Provides graceful error recovery
 * - Logs errors for debugging
 * - Displays beautiful sacred-themed error screen
 * 
 * - User-friendly error messages
 * - Reload functionality
 * - Error details hidden in production
 * - Sacred aesthetic maintained
 * 
 * Usage:
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  /**
   * Log error information
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('🔥 Error Boundary caught an error:', error);
    console.error('📋 Component Stack:', errorInfo.componentStack);

    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might send this to an error reporting service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  /**
   * Reload the page
   */
  handleReload = (): void => {
    window.location.reload();
  };

  /**
   * Reset error state to try again
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default sacred error screen
      return <SacredErrorScreen 
        error={this.state.error} 
        errorInfo={this.state.errorInfo}
        onReload={this.handleReload}
        onReset={this.handleReset}
      />;
    }

    return this.props.children;
  }
}

/**
 * Sacred Error Screen Component
 * Beautiful, calming error display with sacred aesthetic
 */
interface ErrorScreenProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReload: () => void;
  onReset: () => void;
}

const SacredErrorScreen: React.FC<ErrorScreenProps> = ({ 
  error, 
  errorInfo, 
  onReload, 
  onReset 
}) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${SACRED_COLORS.darkCard} 0%, ${SACRED_COLORS.dark} 100%)`,
      color: SACRED_COLORS.light,
      padding: '2rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Sacred Symbol */}
      <div style={{
        fontSize: '4rem',
        marginBottom: '1rem',
        animation: 'pulse 2s ease-in-out infinite'
      }}>
        🌟
      </div>

      {/* Title */}
      <h1 style={{
        fontSize: '2rem',
        fontWeight: 600,
        marginBottom: '0.5rem',
        color: SACRED_COLORS.purple
      }}>
        A Sacred Disruption
      </h1>

      {/* Subtitle */}
      <p style={{
        fontSize: '1.125rem',
        color: SACRED_COLORS.grayLight,
        marginBottom: '2rem',
        maxWidth: '600px',
        textAlign: 'center',
        lineHeight: 1.6
      }}>
        The universe has paused this moment for reflection.
        <br />
        Like a mandala dissolving, we prepare for renewal.
      </p>

      {/* Error Message (User-friendly) */}
      {error && (
        <div style={{
          background: SACRED_COLORS.purpleAlpha(0.1),
          border: `1px solid ${SACRED_COLORS.purpleAlpha(0.3)}`,
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          maxWidth: '600px',
          width: '100%'
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: SACRED_COLORS.red,
            fontWeight: 500,
            marginBottom: '0.5rem'
          }}>
            Error Message:
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: SACRED_COLORS.grayLight,
            fontFamily: 'monospace'
          }}>
            {error.message || 'An unexpected error occurred'}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <button
          onClick={onReset}
          style={{
            padding: '0.75rem 2rem',
            background: SACRED_COLORS.purple,
            color: SACRED_COLORS.white,
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: `0 4px 12px ${SACRED_COLORS.purpleAlpha(0.4)}`
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = `0 6px 16px ${SACRED_COLORS.purpleAlpha(0.5)}`;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = `0 4px 12px ${SACRED_COLORS.purpleAlpha(0.4)}`;
          }}
        >
          Try Again
        </button>

        <button
          onClick={onReload}
          style={{
            padding: '0.75rem 2rem',
            background: 'transparent',
            color: SACRED_COLORS.purple,
            border: `2px solid ${SACRED_COLORS.purple}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = SACRED_COLORS.purpleAlpha(0.1);
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Reload Page
        </button>
      </div>

      {/* Developer Details Toggle */}
      {isDevelopment && (
        <>
          <button
            onClick={() => setShowDetails(!showDetails)}
          style={{
            padding: '0.5rem 1rem',
            background: 'transparent',
            color: SACRED_COLORS.grayLight,
            border: `1px solid ${SACRED_COLORS.grayLight}`,
            borderRadius: '6px',
            fontSize: '0.875rem',
            cursor: 'pointer',
            marginBottom: '1rem',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.color = SACRED_COLORS.light;
            e.currentTarget.style.borderColor = SACRED_COLORS.light;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.color = SACRED_COLORS.grayLight;
            e.currentTarget.style.borderColor = SACRED_COLORS.grayLight;
          }}
          >
            {showDetails ? 'Hide' : 'Show'} Developer Details
          </button>

          {showDetails && errorInfo && (
            <div style={{
              background: SACRED_COLORS.dark,
              border: `1px solid ${SACRED_COLORS.grayLight}`,
              borderRadius: '8px',
              padding: '1rem',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '400px',
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              color: SACRED_COLORS.grayLight
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ color: SACRED_COLORS.emerald }}>Component Stack:</strong>
                <pre style={{ 
                  whiteSpace: 'pre-wrap', 
                  margin: '0.5rem 0 0 0',
                  color: SACRED_COLORS.light
                }}>
                  {errorInfo.componentStack}
                </pre>
              </div>
              
              {error && error.stack && (
                <div>
                  <strong style={{ color: SACRED_COLORS.red }}>Error Stack:</strong>
                  <pre style={{ 
                    whiteSpace: 'pre-wrap', 
                    margin: '0.5rem 0 0 0',
                    color: SACRED_COLORS.light
                  }}>
                    {error.stack}
                  </pre>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Wisdom */}
      <div style={{
        marginTop: '2rem',
        fontSize: '0.875rem',
        color: SACRED_COLORS.grayLight,
        fontStyle: 'italic',
        textAlign: 'center',
        maxWidth: '500px'
      }}>
        "In the space between chaos and order, wisdom emerges."
        <br />
        <span style={{ fontSize: '0.75rem', color: SACRED_COLORS.grayLight }}>
          — Ancient Proverb
        </span>
      </div>

      {/* Animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default ErrorBoundary;
export { SacredErrorScreen };

